
import React from 'react';
import { ExternalLink, ArrowRightLeft, Workflow, Settings, Variable } from 'lucide-react';
import { PropertyField } from '../../molecules/PropertyField';
import { WorkflowNodeData } from '../../../types';

interface CallActivityTabProps {
    data: WorkflowNodeData;
    onChange: (key: string, value: any) => void;
}

export const CallActivityTab: React.FC<CallActivityTabProps> = ({ data, onChange }) => {
    return (
        <div className="p-6 space-y-6">
            <div className="p-3 bg-sky-50 dark:bg-sky-900/20 rounded-lg border border-sky-100 dark:border-sky-900 flex gap-3">
                <Workflow className="text-sky-600 dark:text-sky-400 mt-0.5 shrink-0" size={16} />
                <div className="space-y-1">
                    <p className="text-xs font-bold text-sky-800 dark:text-sky-300">External Process</p>
                    <p className="text-[11px] text-sky-700 dark:text-sky-400 leading-relaxed">
                        Calls another process definition. The parent process waits until the called process completes.
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1 border-b border-slate-200 dark:border-slate-800 pb-2 mb-2">
                        <ExternalLink size={12} /> Target Process
                    </h3>
                    <PropertyField 
                        config={{ 
                            name: 'processCollection', 
                            label: 'Collection', 
                            type: 'text', 
                            placeholder: 'MyCollection',
                            description: 'The collection namespace of the target process.'
                        }}
                        value={data.processCollection}
                        onChange={onChange}
                    />
                    <PropertyField 
                        config={{ 
                            name: 'processName', 
                            label: 'Process Name', 
                            type: 'text', 
                            placeholder: 'MyProcess/sub1',
                            validation: { required: true },
                            description: 'The unique name of the target process.'
                        }}
                        value={data.processName}
                        onChange={onChange}
                    />
                    <PropertyField 
                        config={{ 
                            name: 'processVersion', 
                            label: 'Version', 
                            type: 'number', 
                            placeholder: '0',
                            description: '0 for current, -1 for latest.'
                        }}
                        value={data.processVersion}
                        onChange={onChange}
                    />
                </div>

                <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1 border-b border-slate-200 dark:border-slate-800 pb-2 mb-2">
                        <Settings size={12} /> Execution Settings
                    </h3>
                    <PropertyField 
                        config={{ 
                            name: 'keepBusinessKey', 
                            label: 'Keep Business Key', 
                            type: 'boolean',
                            description: 'Propagate the parent process business key to the child process.'
                        }}
                        value={data.keepBusinessKey}
                        onChange={onChange}
                    />
                    <PropertyField 
                        config={{ 
                            name: 'sequentialLoop', 
                            label: 'Sequential Loop', 
                            type: 'boolean',
                            description: 'If looping, execute iterations sequentially instead of parallel.'
                        }}
                        value={data.sequentialLoop}
                        onChange={onChange}
                    />
                    <PropertyField 
                        config={{ 
                            name: 'asAsyncCall', 
                            label: 'As Async Call', 
                            type: 'boolean',
                            description: 'Execute asynchronously (fire and forget).'
                        }}
                        value={data.asAsyncCall}
                        onChange={onChange}
                    />
                </div>

                <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1 border-b border-slate-200 dark:border-slate-800 pb-2 mb-2">
                        <Variable size={12} /> Result
                    </h3>
                    <PropertyField
                        config={{
                            name: 'resultVariable',
                            label: 'Result Variable',
                            type: 'text',
                            placeholder: 'callResult',
                            description: 'Variable to store the returned values from the called process as an object.'
                        }}
                        value={data.resultVariable}
                        onChange={onChange}
                    />
                </div>

                <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-4">
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200 dark:border-slate-800 pb-2 mb-2">
                        <ArrowRightLeft size={12} /> Data Mapping
                    </div>

                    <PropertyField 
                        config={{ 
                            name: 'inputVariables', 
                            label: 'Input Variables (Map to Child)', 
                            type: 'object', 
                            rows: 4,
                            placeholder: '{\n  "childVar": "${parentVar}"\n}',
                            description: 'Map parent variables to child process inputs.'
                        }}
                        value={data.inputVariables}
                        onChange={onChange}
                    />

                    <PropertyField 
                        config={{ 
                            name: 'outputVariables', 
                            label: 'Output Variables (Map from Child)', 
                            type: 'object', 
                            rows: 4,
                            placeholder: '{\n  "parentVar": "${childResult}"\n}',
                            description: 'Map child process outputs back to parent variables.'
                        }}
                        value={data.outputVariables}
                        onChange={onChange}
                    />
                </div>
            </div>
        </div>
    );
};
